import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PageHeader from './PageHeader.jsx';
import Loader, { ErrorBox } from './Loader.jsx';
import OrganisationAutocomplete from './OrganisationAutocomplete.jsx';
import SetPasswordModal from './SetPasswordModal.jsx';
import { KeyIcon } from './Icons.jsx';
import { notify } from '../lib/notifications';
import { useAuth } from '../lib/auth';


function extraParams(resource, user) {
  if (typeof resource.listParams === 'function') return resource.listParams(user) || {};
  if (resource.listParams && typeof resource.listParams === 'object') return { ...resource.listParams };
  return {};
}

function visibleFields(resource, user) {
  const raw = typeof resource.fields === 'function' ? resource.fields(user) : resource.fields;
  return (raw || []).filter((f) => !(typeof f.hideIf === 'function' && f.hideIf(user)));
}



/**
 * Generic CRUD form.
 *
 * Renders one input per `resource.fields[i]`.  Supported field types:
 *
 *   - text / email / tel / password / number / date  (HTML <input>)
 *   - textarea                                       (HTML <textarea>)
 *   - checkbox                                       (HTML checkbox)
 *   - select       — `loader: () => Promise<list>` *or* static `options`
 *   - multiselect  — same as select but renders a multi-pick <select>
 *
 * Mode:
 *   - `mode === 'create'` mounts an empty form, then POSTs.
 *   - `mode === 'edit'`   first GETs the record (via :id), then PATCHes.
 *
 * Optional `resource.setPassword(id, password)` — when present, the
 * edit view shows a "Change password" button that opens a dedicated
 * dialog (used by Business → Users).
 */
export default function ResourceForm({ resource, basePath, mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const fields = useMemo(() => visibleFields(resource, currentUser), [resource, currentUser]);
  const queryParams = useMemo(() => extraParams(resource, currentUser), [resource, currentUser]);

  const initial = useMemo(() => {
    // Today's date in YYYY-MM-DD form, used as the default for any
    // ``type: 'date'`` field that doesn't supply its own ``default``.
    // Pre-filling new resource forms with today's date saves the user
    // a click and matches the "today's date as default" UX rule
    // applied across the rest of the app (TaskForm,
    // RoutineTaskForm…).  Edit-mode still wins because the existing
    // record overrides this in the load-effect below.
    const today = new Date().toISOString().slice(0, 10);
    const o = {};
    fields.forEach((f) => {
      if (f.type === 'checkbox')          o[f.name] = !!f.default;
      else if (f.type === 'multiselect')  o[f.name] = [];
      else if (f.type === 'date')         o[f.name] = f.default ?? today;
      else                                 o[f.name] = f.default ?? '';
    });
    return o;
  }, [fields]);


  const [form, setForm] = useState(initial);
  const [detailsLocked, setDetailsLocked] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const canSetPassword = mode === 'edit' && typeof resource.setPassword === 'function';

  // For each "loader" field, the choices it fetched.
  const [choices, setChoices] = useState({});

  // 1. Load existing record (edit mode only).
  useEffect(() => {
    if (mode !== 'edit') return;
    let cancelled = false;
    resource.api.retrieve(id, queryParams)
      .then((data) => {
        if (cancelled) return;
        const next = { ...initial };
        Object.keys(data).forEach((k) => {
          if (k in next || fields.some((f) => f.name === k)) {
            next[k] = data[k] === null ? '' : data[k];
          }
        });
        // Bool fields must remain real booleans for the checkbox.
        fields.forEach((f) => {
          if (f.type === 'checkbox') next[f.name] = !!data[f.name];
          if (f.type === 'multiselect') {
            const v = data[f.name];
            next[f.name] = Array.isArray(v) ? v : [];
          }
        });
        setForm(next);
        if (resource.detailsLock) setDetailsLocked(!!data.details_locked);
      })
      .catch(setError)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, mode, resource]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Run the loaders for any select / multiselect fields.
  useEffect(() => {
    let cancelled = false;
    fields.forEach((f) => {
      if (typeof f.loader === 'function') {
        f.loader()
          .then((list) => { if (!cancelled) setChoices((c) => ({ ...c, [f.name]: list })); })
          .catch(() => { if (!cancelled) setChoices((c) => ({ ...c, [f.name]: [] })); });
      }
    });
    return () => { cancelled = true; };
  }, [fields]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (detailsLocked) {
      setError('This organisation is locked. Unlock it with the PIN before editing its details.');
      return;
    }
    setSubmitting(true);
    setError(null);

    // Strip empty strings on optional fields so the backend doesn't
    // reject "" for FK / numeric columns.
    const payload = {};
    fields.forEach((f) => {
      if (f.onlyOnCreate && mode !== 'create') return;
      const v = form[f.name];
      if (v === '' || v === null || v === undefined) {
        // Skip empty values entirely (DRF treats missing as no-change on PATCH).
        return;
      }
      payload[f.name] = v;
    });

    try {
      let saved;
      if (mode === 'create') saved = await resource.api.create(payload);
      else                   saved = await resource.api.update(id, payload, queryParams);
      // Friendly success toast — the brief calls for an explicit
      // success message + redirect to the list view after a successful
      // create / update.  Field-level 400 errors are still surfaced
      // inline via <ErrorBox> below.
      notify.success(
        mode === 'create'
          ? `${resource.singular} created successfully.`
          : `${resource.singular} saved.`,
      );
      navigate(`${basePath}/${resource.slug}`);
      return saved;
    } catch (err) {
      // The axios interceptor already toasts unexpected 5xx / network
      // errors.  We render the per-field 400 details inline via
      // ``<ErrorBox error={error} />`` so the user sees exactly which
      // field the server rejected — including the new
      // ``organisation`` field's "required" / "not found" / "schema
      // not provisioned" messages from ``UserViewSet.create``.
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };


  if (loading) return <Loader />;

  return (
    <>
      <PageHeader
        title={`${mode === 'create' ? 'New' : 'Edit'} ${resource.singular}`}
        subtitle={`Manage ${resource.label.toLowerCase()}.`}
        actions={canSetPassword ? (
          <button
            type="button"
            className="ty-btn ty-btn--secondary"
            onClick={() => setPwdOpen(true)}
          >
            <KeyIcon size={16} aria-hidden />
            Change password
          </button>
        ) : undefined}
      />
      <ErrorBox error={error} />

      {detailsLocked && (
        <div className="ty-lock-banner" role="status">
          Details are locked. People in this organisation can keep working.
          Unlock it from the organisations list with the PIN before editing or deleting.
        </div>
      )}

      <form onSubmit={onSubmit} className="ty-card">
        <fieldset disabled={detailsLocked} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}>
        <div className="ty-grid-2">
          {fields.map((f) => {
            if (f.onlyOnCreate && mode !== 'create') return null;

            // Common props (used by single-line inputs only).
            const inputProps = {
              id:        f.name,
              className: 'ty-input',
              value:     form[f.name] ?? '',
              required:  !!f.required,
              placeholder: f.placeholder,
              onChange:  (e) => set(f.name, e.target.value),
            };

            return (
              <div className="ty-field" key={f.name} style={f.type === 'textarea' || f.type === 'multiselect' ? { gridColumn: '1 / -1' } : undefined}>
                <label htmlFor={f.name}>
                  {f.label}{f.required ? ' *' : ''}
                </label>

                {f.type === 'org-autocomplete' && (
                  // Server-side searchable Organisation dropdown
                  // (debounced, 10 results per query).  See
                  // ``OrganisationAutocomplete.jsx`` for the full
                  // contract.  We pass the current id as ``value``
                  // and store the picked id back into the form
                  // state — the existing onSubmit serialisation
                  // already strips empty FKs.
                  <OrganisationAutocomplete
                    id={f.name}
                    value={form[f.name]}
                    required={!!f.required}
                    placeholder={f.placeholder}
                    onChange={(orgId) => set(f.name, orgId)}
                  />
                )}

                {f.type === 'textarea' && (

                  <textarea
                    id={f.name}
                    className="ty-textarea"
                    value={form[f.name] || ''}
                    onChange={(e) => set(f.name, e.target.value)}
                  />
                )}

                {f.type === 'checkbox' && (
                  <label className="ty-row" style={{ gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!form[f.name]}
                      onChange={(e) => set(f.name, e.target.checked)}
                    />
                    <span>{f.help || 'Enabled'}</span>
                  </label>
                )}

                {f.type === 'select' && (
                  <select
                    id={f.name}
                    className="ty-select"
                    value={form[f.name] ?? ''}
                    required={!!f.required}
                    onChange={(e) => set(f.name, e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {(f.options || (choices[f.name] || [])).map((opt) => {
                      // Static options have shape { value, label }.
                      // Loader rows are full objects; use optionLabel.
                      const isStatic = 'value' in opt && 'label' in opt;
                      const value = isStatic ? opt.value : opt.id;
                      const label = isStatic ? opt.label : (f.optionLabel ? f.optionLabel(opt) : (opt.name || opt.email || opt.id));
                      return <option key={value} value={value}>{label}</option>;
                    })}
                  </select>
                )}

                {f.type === 'multiselect' && (
                  <select
                    id={f.name}
                    className="ty-select"
                    multiple
                    value={(form[f.name] || []).map(String)}
                    onChange={(e) => {
                      const vals = Array.from(e.target.selectedOptions).map((o) => Number(o.value) || o.value);
                      set(f.name, vals);
                    }}
                    style={{ minHeight: 140 }}
                  >
                    {(f.options || (choices[f.name] || [])).map((opt) => {
                      const isStatic = 'value' in opt && 'label' in opt;
                      const value = isStatic ? opt.value : opt.id;
                      const label = isStatic ? opt.label : (f.optionLabel ? f.optionLabel(opt) : (opt.name || opt.email || opt.id));
                      return <option key={value} value={value}>{label}</option>;
                    })}
                  </select>
                )}

                {(!['textarea', 'checkbox', 'select', 'multiselect', 'org-autocomplete'].includes(f.type)) && (
                  <input
                    type={f.type || 'text'}
                    step={f.step}
                    {...inputProps}
                    autoComplete={f.type === 'password' ? 'new-password' : undefined}
                  />
                )}


                {f.help && f.type !== 'checkbox' && (
                  <small className="ty-muted">{f.help}</small>
                )}
              </div>
            );
          })}
        </div>
        </fieldset>

        <div className="ty-row ty-row--end ty-mt-2">
          <button type="button" className="ty-btn ty-btn--ghost"
                  onClick={() => navigate(`${basePath}/${resource.slug}`)}>
            Cancel
          </button>
          {canSetPassword && (
            <button
              type="button"
              className="ty-btn ty-btn--secondary"
              onClick={() => setPwdOpen(true)}
            >
              <KeyIcon size={16} aria-hidden />
              Change password
            </button>
          )}
          {!detailsLocked && (
            <button type="submit" className="ty-btn" disabled={submitting}>
              {submitting ? 'Saving…' : `Save ${resource.singular}`}
            </button>
          )}
        </div>
      </form>

      {pwdOpen && canSetPassword && (
        <SetPasswordModal
          userId={id}
          userLabel={
            [form.first_name, form.last_name].filter(Boolean).join(' ').trim()
            || form.email
            || form.username
            || `#${id}`
          }
          setPassword={(uid, password) => resource.setPassword(uid, password, queryParams)}
          onClose={() => setPwdOpen(false)}
        />
      )}
    </>
  );
}
